/**
 * Page Assistant Vide-Frigo — point d'entrée refactoré
 * ~80 lignes : compose hooks, services et composants
 */

import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import RecipeDetailsModal from "../../../components/recipe/RecipeDetailsModal";
import { useFridgeData } from "./hooks/useFridgeData";
import { useTranslatedLabels } from "./hooks/useTranslatedLabels";
import { useFridgeSearch } from "./hooks/useFridgeSearch";
import {
  commonIngredients,
  quickStartIngredients,
} from "./constants/ingredients";
import FridgeHeader from "./components/FridgeHeader";
import IngredientsPanel from "./components/IngredientsPanel";
import MatchFilterSection from "./components/MatchFilterSection";
import LoadingState from "./components/LoadingState";
import EmptyState from "./components/EmptyState";
import NoResultsState from "./components/NoResultsState";
import RecipeResultsGrid from "./components/RecipeResultsGrid";

export default function FridgeAssistantPage() {
  const { t, language } = useLanguage();
  const { categories, countries } = useFridgeData();
  const { getIngredientLabel } = useTranslatedLabels(language);

  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [currentIngredient, setCurrentIngredient] = useState("");
  const [matchingRecipes, setMatchingRecipes] = useState([]);
  const [searching, setSearching] = useState(false);
  const [minMatchPercentage, setMinMatchPercentage] = useState(0);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showIngredientSuggestions, setShowIngredientSuggestions] =
    useState(false);
  const ingredientSuggestionsBlurTimer = useRef(null);

  const ingredientSuggestionsList = useMemo(() => {
    const term = (currentIngredient || "").trim().toLowerCase();
    if (term.length === 0) return [];
    const all = [...new Set([...quickStartIngredients, ...commonIngredients])];
    return all
      .filter(
        (ing) =>
          !availableIngredients.includes(ing) &&
          (getIngredientLabel(ing).toLowerCase().includes(term) ||
            ing.toLowerCase().includes(term)),
      )
      .slice(0, 12);
  }, [currentIngredient, availableIngredients, getIngredientLabel]);

  const handleIngredientInputFocus = useCallback(() => {
    if (ingredientSuggestionsBlurTimer.current) {
      clearTimeout(ingredientSuggestionsBlurTimer.current);
      ingredientSuggestionsBlurTimer.current = null;
    }
    if (currentIngredient.trim() && ingredientSuggestionsList.length > 0) {
      setShowIngredientSuggestions(true);
    }
  }, [currentIngredient, ingredientSuggestionsList.length]);

  const handleIngredientInputBlur = useCallback(() => {
    ingredientSuggestionsBlurTimer.current = setTimeout(() => {
      setShowIngredientSuggestions(false);
    }, 200);
  }, []);

  const applyIngredientSuggestion = useCallback((ingredient) => {
    setAvailableIngredients((prev) =>
      prev.includes(ingredient) ? prev : [...prev, ingredient],
    );
    setCurrentIngredient("");
    setShowIngredientSuggestions(false);
  }, []);

  const runSearch = useFridgeSearch(
    availableIngredients,
    minMatchPercentage,
    categories,
    countries,
    setMatchingRecipes,
  );

  useEffect(() => {
    if (availableIngredients.length === 0) {
      setMatchingRecipes([]);
      return;
    }
    setSearching(true);
    runSearch().finally(() => setSearching(false));
  }, [availableIngredients, minMatchPercentage, runSearch]);

  const addIngredient = useCallback((ingredient) => {
    const trimmed = ingredient.trim();
    if (!trimmed) return;
    setAvailableIngredients((prev) =>
      prev.includes(trimmed) ? prev : [...prev, trimmed],
    );
    setCurrentIngredient("");
  }, []);

  const removeIngredient = useCallback((ingredient) => {
    setAvailableIngredients((prev) => prev.filter((i) => i !== ingredient));
  }, []);

  return (
    <div className="min-h-full py-4 sm:py-6 w-full min-w-0 overflow-x-hidden">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6">
        <FridgeHeader t={t} />
        <IngredientsPanel
          currentIngredient={currentIngredient}
          setCurrentIngredient={setCurrentIngredient}
          showIngredientSuggestions={showIngredientSuggestions}
          setShowIngredientSuggestions={setShowIngredientSuggestions}
          ingredientSuggestionsList={ingredientSuggestionsList}
          handleIngredientInputFocus={handleIngredientInputFocus}
          handleIngredientInputBlur={handleIngredientInputBlur}
          applyIngredientSuggestion={applyIngredientSuggestion}
          addIngredient={addIngredient}
          availableIngredients={availableIngredients}
          setAvailableIngredients={setAvailableIngredients}
          removeIngredient={removeIngredient}
          getIngredientLabel={getIngredientLabel}
          t={t}
        />
        <MatchFilterSection
          minMatchPercentage={minMatchPercentage}
          setMinMatchPercentage={setMinMatchPercentage}
          t={t}
          visible={availableIngredients.length > 0 && !searching}
        />
        {searching ? (
          <LoadingState t={t} />
        ) : availableIngredients.length === 0 ? (
          <EmptyState
            addIngredient={addIngredient}
            getIngredientLabel={getIngredientLabel}
            t={t}
          />
        ) : matchingRecipes.length === 0 ? (
          <NoResultsState t={t} />
        ) : (
          <RecipeResultsGrid
            matchingRecipes={matchingRecipes}
            setSelectedRecipe={setSelectedRecipe}
            t={t}
          />
        )}
        {selectedRecipe && (
          <RecipeDetailsModal
            recipe={selectedRecipe}
            onClose={() => setSelectedRecipe(null)}
          />
        )}
      </div>
    </div>
  );
}
